import { useLocation, useNavigate } from "react-router-dom";
import { HiOutlineMinusSm } from "react-icons/hi";
import { FormattedMessage } from "react-intl";
import { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";

import "./FAQ.css";

type QuestionType =
	| "fba"
	| "dropshipping"
	| "shopifyStore"
	| "amazonIntegration"
	| "shopifyIntegration"
	| "productImport"
	| "updatePricing"
	| "productVisibility"
	| "bulkEdit"
	| "pricingOptions"
	| "sync"
	| "statusCheck"
	| "productSearch"
	| "expandMySalesChannels"
	| "orderOnPlatform"
	| "placeProducts";

const FAQ = () => {
	const [expandedItem, setExpandedItem] = useState<QuestionType | null>(null);

	const location = useLocation();
	const navigate = useNavigate();

	const handleExpandItem = (item: QuestionType) => {
		if (expandedItem === item) {
			setExpandedItem(null);
			navigate(location.pathname, { replace: true });
		} else {
			setExpandedItem(item);
			navigate(`${location.pathname}#${item}`, { replace: true });
		}
	};

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (window.location.hash) {
			const question = window.location.hash.replace(
				"#",
				""
			) as QuestionType;

			setExpandedItem(question);

			setTimeout(() => {
				const element = document.getElementById(question);
				if (element) {
					const yOffset = -24;
					const y =
						element.getBoundingClientRect().top +
						window.pageYOffset +
						yOffset;

					window.scrollTo({
						top: y,
						behavior: "smooth",
					});
				}
			}, 100);
		} else {
			setExpandedItem(null);
		}
	}, []);

	return (
		<div className="min-block-height d-flex flex-column justify-content-center align-items-center bg-white px-4">
			<div data-testid="content" className="faq-content-wrapper">
				<div className="faq-title-block">
					<h5 className="faq-title">
						<FormattedMessage id="frequentlyAskedQuestions" />
					</h5>
					<p className="faq-description">
						<FormattedMessage id="hereAreSomeOfTheMostFrequentlyAskedQuestions" />
					</p>
				</div>
				<div className="faq-questions-list">
					<div
						id="fba"
						className={`faq-question-item ${expandedItem === "fba" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIGetPaidAsAnFBASeller" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="whenTheOrderIsFullyExecutedThenTheMoneyWillBeTransferred" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("fba")}
						>
							<GoPlus
								data-testid="plus-icon"
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								data-testid="minus-icon"
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="dropshipping"
						className={`faq-question-item ${expandedItem === "dropshipping" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howAmIGetChargedToDropshipProductsFromCloudOrder" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="asASellerYouWillNeedToAddACreditCardToYourCloudOrderAccount" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouPreferNotToUseAutomaticPaymentsYouCanOptForManualPayments" />
								<br />
								<br />-{" "}
								<FormattedMessage id="navigateToTheOrdersSectionInTheMainMenuOnTheLeft" />
								<br />-{" "}
								<FormattedMessage id="selectTheOrderYouWishToPayFor" />
								<br />-{" "}
								<FormattedMessage id="clickTheSelectButtonOnTheLeftThenChoosePayManually" />
								<br />-{" "}
								<FormattedMessage id="chooseOneOfTheProvidedMethodsToCompleteThePayment" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("dropshipping")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="shopifyStore"
						className={`faq-question-item ${expandedItem === "shopifyStore" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="ifIAnAnFBASellerAndHaveShopifyStore" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="asAnFbaSellerWithAShopifyStoreYouCanIntegrateBothYourAmazonSellerCentralAndShopify" />
								<p>&nbsp;</p>
								<FormattedMessage id="theTransactionFeeIsAutomaticallyChargedToYourDefaultPaymentMethod" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("shopifyStore")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="amazonIntegration"
						className={`faq-question-item ${expandedItem === "amazonIntegration" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIConnectCloudOrderToMyAmazonStore" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="connectingCloudOrderToYourAmazonStoreAllowsYouToFulfillOrdersFromYourShopify" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="logInToYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLowerLeftCornerClickTheSettingsIcon" />
								<br />-{" "}
								<FormattedMessage id="inTheSettingsMenuLocateTheIntegrationTableAndClickAddNew" />
								<br />-{" "}
								<FormattedMessage id="fromTheListOfAvailableIntegrationsSelectAmazonMCF" />
								<br />-{" "}
								<FormattedMessage id="followTheOnScreenInstructionsToConnectYourAmazonSellerCentralAccount" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouEncounterAnyIssuesOurSupportTeamIsHereToHelp" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() =>
								handleExpandItem("amazonIntegration")
							}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="shopifyIntegration"
						className={`faq-question-item ${expandedItem === "shopifyIntegration" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIConnectCloudOrderToMyShopifyStore" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="connectingYourShopifyStoreToCloudOrderAllowsYouToSeamlesslyImportOrders" />
								<p>&nbsp;</p>
								<FormattedMessage id="methodOneInstallCloudOrderViaTheShopifyAppStore" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="logInToYourShopifyAdminPanel" />
								<br />-{" "}
								<FormattedMessage id="visitTheShopifyAppStoreAndSearchForCloudOrder" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="https://apps.shopify.com/multi-channel-drop-shipping"
								>
									https://apps.shopify.com/multi-channel-drop-shipping
								</a>
								)
								<br />-{" "}
								<FormattedMessage id="clickInstallAndFollowOnScreenInstructions" />
								<br />-{" "}
								<FormattedMessage id="onceInstalledYourShopifyStoreWillBeAutomaticallyConnectedToCloudOrder" />
								<p>&nbsp;</p>
								<FormattedMessage id="methodTwoAddAShopifyIntegrationInCloudOrder" />
								<p>&nbsp;</p>
								<FormattedMessage id="logInToYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLowerLeftCornerClickTheSettingsIcon" />
								<br />-{" "}
								<FormattedMessage id="inTheSettingsMenuLocateTheIntegrationTableAndClickAddNew" />
								<br />-{" "}
								<FormattedMessage id="fromTheListOfAvailableIntegrationsSelectShopify" />
								<br />-{" "}
								<FormattedMessage id="enterYourShopifyStoreNameAndClickConnect" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouEncounterAnyIssuesOurSupportTeamIsHereToHelp" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() =>
								handleExpandItem("shopifyIntegration")
							}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="productImport"
						className={`faq-question-item ${expandedItem === "productImport" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIImportProductsFromMyAmazonStoreToCloudOrder" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								-{" "}
								<FormattedMessage id="loginIntoYourCloudOrderTenant" />
								<br />-{" "}
								<FormattedMessage id="inTheLowerLeftCornerSelectTheSettings" />
								<br />-{" "}
								<FormattedMessage id="onTheFollowingPageClickSelectOnTheRightSideOfTheRequiredIntegration" />
								<br />-{" "}
								<FormattedMessage id="fromTheListOfProposedActionsSelectTheImportAndConfirmTheAction" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("productImport")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="updatePricing"
						className={`faq-question-item ${expandedItem === "updatePricing" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIChangeTheProductCostPricingForMyProductsListedOnCloudOrder" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="aToManuallyChangeTheCostForASingleProduct" />
								<br />-{" "}
								<FormattedMessage id="loginIntoYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuSelectTheMyProducts" />
								<br />-{" "}
								<FormattedMessage id="openProductVariantDetailsPage" />
								<br />-{" "}
								<FormattedMessage id="clickActionsButtonThenSelectEditProduct" />
								<br />
								<p>&nbsp;</p>
								<FormattedMessage id="bToChangeThePricingForBulkProductList" />
								<br />-{" "}
								<FormattedMessage id="loginIntoYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuSelectTheMyProducts" />
								<br />-{" "}
								<FormattedMessage id="locateTheBulkUpdateButton" />
								<br />-{" "}
								<FormattedMessage id="downloadProductsFileTemplate" />
								<br />-{" "}
								<FormattedMessage id="updateTheCostColumn" />
								<br />-{" "}
								<FormattedMessage id="uploadTheUpdatedFile" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("updatePricing")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="productVisibility"
						className={`faq-question-item ${expandedItem === "productVisibility" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="whyAreNotMyProductsShowingUpInCloudOrderCatalog" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="whenYouImportProductsIntoCloudOrderTheyAreSetToPrivateByDefault" />
								<p>&nbsp;</p>
								<FormattedMessage id="optionOneMakeAProductsPublicViaUserInterface" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="logInToYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuSelectTheMyProducts" />
								<br />-{" "}
								<FormattedMessage id="onMyProductsPageLocateTheProductYouWantToMakePublicAndClickCheckbox" />
								<br />-{" "}
								<FormattedMessage id="fromListOfActionsChooseMakePublic" />
								<br />-{" "}
								<FormattedMessage id="allowUpTo24hoursForOurTeamToReviewAndApproveYourRequest" />
								<p>&nbsp;</p>
								<FormattedMessage id="optionTwoMakeAAproductsPublicByUpdatingProductsFile" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="loginIntoYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuSelectTheMyProducts" />
								<br />-{" "}
								<FormattedMessage id="locateTheBulkUpdateButton" />
								<br />-{" "}
								<FormattedMessage id="downloadProductsFileTemplate" />
								<br />-{" "}
								<FormattedMessage id="updateThePublicColumn" />
								<br />-{" "}
								<FormattedMessage id="uploadTheUpdatedFile" />
								<br />-{" "}
								<FormattedMessage id="allowUpTo24hoursForOurTeamToReviewAndApproveYourRequest" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYourProductsAreNotApprovedOrYouHaveQuestionAboutTHeReviewReachOutToOurTeam" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() =>
								handleExpandItem("productVisibility")
							}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="bulkEdit"
						className={`faq-question-item ${expandedItem === "bulkEdit" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="iHaveALotOfSKUsHowDoIBulkEditTheCostPriceOfMyProducts" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								-{" "}
								<FormattedMessage id="loginIntoYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuSelectTheMyProducts" />
								<br />-{" "}
								<FormattedMessage id="locateTheBulkUpdateButton" />
								<br />-{" "}
								<FormattedMessage id="downloadProductsFileTemplate" />
								<br />-{" "}
								<FormattedMessage id="updateTheCostColumn" />
								<br />-{" "}
								<FormattedMessage id="uploadTheUpdatedFile" />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("bulkEdit")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="pricingOptions"
						className={`faq-question-item ${expandedItem === "pricingOptions" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="whatAreTheDifferentShippingTiersAndShippingRates" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="cloudOrderIntegrateWithAmazonMultiChannelFulfillment" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="standartByDefaultCloudOrderMapsAllShopifyDeliveryMethods" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="expeditedAvailableForShopifyStoreOwnersToAdd" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="priorityAlsoAvailableForShopifyStoreOwnersToAdd" />
								<p>&nbsp;</p>
								<FormattedMessage id="shippingRatesShippingFeesAreCalculatedByAmazonMcfBasedOnTheWeight" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="https://supplychain.amazon.com/pricing"
								>
									https://supplychain.amazon.com/pricing
								</a>{" "}
								<FormattedMessage id="noteThatIfYouOfferFreeShippingOnYourShopifyStoreYouWillBeResponsible" />
								<p>&nbsp;</p>
								<FormattedMessage id="howFeesAreUpdatedAfterAmazonFulfillsYourOrder" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("pricingOptions")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="sync"
						className={`faq-question-item ${expandedItem === "sync" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howOftenAreMyProductsSyncedFromAmazon" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="ifYouHaveIntegratedYourAmazonSellerCentralAccountWithCloudOrderYourProductsAreAutomaticallySynced" />
								<p>&nbsp;</p>
								<FormattedMessage id="toManuallyUpdateExistingProductsFollowTheseSteps" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="logInToYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLowerLeftCornerClickTheSettingsIcon" />
								<br />-{" "}
								<FormattedMessage id="inTheSettingsMenuLocateTheIntegrationTableChooseYourAmazonIntegrationAndClickImport" />
								<br />-{" "}
								<FormattedMessage id="allowAFewMinutesForTheSyncToCompleteYouWillReceiveANotification" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouEncounterAnyIssuesOurSupportTeamIsHereToHelp" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("sync")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="statusCheck"
						className={`faq-question-item ${expandedItem === "statusCheck" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoICheckTheStatusOfMyProductsImportsFromAmazon" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="afterIntegratingYourAmazonSellerCentralAccountWithCloudOrderYourInitialProductImportWillBeginAutomatically" />
								<p>&nbsp;</p>
								<FormattedMessage id="onceTheImportIsCompleteYouWillReceiveANotificationAndYourProducts" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("statusCheck")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						id="productSearch"
						className={`faq-question-item ${expandedItem === "productSearch" ? "expanded" : ""}`}
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIFindASpecificCategoryOfProductsToDropshipForMyShopify" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="cloudOrdersCatalogMakesItEasyForShopifyStoreOwners" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouHaveACloudOrderAccount" />
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="logInToYourCloudOrderAccount" />
								<br />-{" "}
								<FormattedMessage id="inTheLeftMenuClickCatalog" />
								<br />-{" "}
								<FormattedMessage id="onTheCatalogPageYouCan" />
								<p>&nbsp;</p>
								<p style={{ paddingLeft: "16px" }}>
									1){" "}
									<FormattedMessage id="filterByCategoryClickTheCategoryDropdownInTheTopNavigation" />
									<br />
									2){" "}
									<FormattedMessage id="searchByNameUseTheSearchBarAtTheTopOfThePage" />
									<br />
									3){" "}
									<FormattedMessage id="searchBySkuInTheSameSearchBarEnterTheProductSku" />
								</p>
								<p>&nbsp;</p>-{" "}
								<FormattedMessage id="browseTheFilteredResultsToFindProductsThatSuitYourStore" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouDoNotHaveACloudOrderAccount" />
								<p>&nbsp;</p>
								<FormattedMessage id="youCanExploreOurPublicCatalogWithoutAnAccountByVisiting" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="https://cloudorder.us/catalog"
								>
									https://cloudorder.us/catalog
								</a>{" "}
								<FormattedMessage id="useTheSameFilteringAndSearchOptionsToBrowseAvailableProducts" />
								<p>&nbsp;</p>
								<FormattedMessage id="ifYouEncounterAnyIssuesOurSupportTeamIsHereToHelp" />{" "}
								<a
									target="_blank"
									rel="noreferrer"
									className="faq-link"
									href="mailto:support@cloudorder.us"
								>
									support@cloudorder.us
								</a>
								.
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("productSearch")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						className={`faq-question-item  ${
							expandedItem === "expandMySalesChannels"
								? "expanded"
								: ""
						}`}
						id="expandMySalesChannels"
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="asABrandOwnerOrSupplierHowDoIUseCloudOrderToExpandMySalesChannels" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="cloudOrderProvidesAPowerfulPlatformToGrowYourReach" />
								<br />
								<FormattedMessage id="here’sHowItWorks" />
								<br />
								<br />
								<FormattedMessage id="currentSalesChannelsAvailableOnCloudOrder" />
								<br />
								<br />-{" "}
								<FormattedMessage id="shopifyStoresAnyProductYouListOn" />
								<br />-{" "}
								<FormattedMessage id="tikTokShopsYourListedProducts" />
								<br />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() =>
								handleExpandItem("expandMySalesChannels")
							}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						className={`faq-question-item  ${
							expandedItem === "orderOnPlatform" ? "expanded" : ""
						}`}
						id="orderOnPlatform"
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="asABrandOwnerOrSupplierHowDoIFulfillTheOrdersReceivedOnThePlatform" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="cloudOrderPartnersWithFulfillmentServicesLikeKYT" />
								<br />
								<FormattedMessage id="onceLinkedYourOrdersWillBeFulfilledAutomatically" />
								<br />
								<FormattedMessage id="thisIntegrationEnsures" />
								<br />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("orderOnPlatform")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
					<div
						className={`faq-question-item  ${
							expandedItem === "placeProducts" ? "expanded" : ""
						}`}
						id="placeProducts"
					>
						<div className="faq-question-circle-wrapper">
							<div className="faq-question-circle"></div>
						</div>
						<div className="faq-question-content">
							<h5 className="faq-question-title">
								<FormattedMessage id="howDoIListMyProductsOnCloudOrder" />
							</h5>
							<p className="faq-question-description">
								<div className="faq-question-description-line"></div>
								<FormattedMessage id="listingYourProducts" />
								<br />
								<FormattedMessage id="youHaveTwoOptions" />
								<br />
								<br />
								1. <FormattedMessage id="manualBulkUpload" />
								<br />
								<br />
								2.{" "}
								<FormattedMessage id="amazonSellerCentralIntegration" />
								<br />
							</p>
						</div>
						<div
							className="faq-question-icon"
							onClick={() => handleExpandItem("placeProducts")}
						>
							<GoPlus
								className="faq-question-plus-icon"
								size={35}
								color="#000000"
							/>
							<HiOutlineMinusSm
								className="faq-question-minus-icon"
								size={35}
								color="#000000"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
export default FAQ;
